import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
    Truck, Plus, Warning, CheckCircle, Wrench, X, PencilSimple, Trash, Clock, Power,
    Bell, ChartBar, Money, GasPump, Calendar, ArrowsCounterClockwise, MagnifyingGlass,
} from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import FleetFuelReport from "./FleetFuelReport";
import Transporters from "./Transporters";
import VehicleScanner from "./VehicleScanner";
import FleetLicenseAlerts from "./FleetLicenseAlerts";
import { fmtDuration } from "../lib/duration";

const CATEGORY_OPTIONS = [
    { value: "fel", label: "FEL", short: "FEL" },
    { value: "adt", label: "ADT (Articulated Dump Truck)", short: "ADT" },
    { value: "excavator", label: "Excavator", short: "Excavator" },
    { value: "forklift", label: "Forklift", short: "Forklift" },
    { value: "passenger_vehicle", label: "Passenger Vehicle", short: "Passenger" },
];

const CATEGORY_LABEL = Object.fromEntries(CATEGORY_OPTIONS.map((c) => [c.value, c.short]));

const STATUS_LABEL = {
    ok: { label: "OK", colour: "var(--grindrod-green)", bg: "var(--grindrod-green-bg)" },
    service_warning: { label: "Service due ≤ 36h", colour: "#FFB300", bg: "#FFF8E1" },
    service_overdue: { label: "Service overdue", colour: "var(--grindrod-red)", bg: "#FDE7E9" },
    out_of_service: { label: "Out of service", colour: "#64748B", bg: "#F1F5F9" },
};

// Decimal hours → HH:MM
function fmtHM(hours) {
    if (hours == null || isNaN(hours)) return "00:00";
    const total = Math.max(0, Math.round(Number(hours) * 60));
    const h = Math.floor(total / 60);
    const m = total % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
// Minutes → human duration ("1h:30min")
function fmtMinHM(minutes) {
    return fmtDuration(minutes);
}

const fmtZAR = (n) =>
    "R " + (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

export default function FleetDashboard() {
    const { user } = useAuth();
    const canEdit = ["admin", "fleet_manager"].includes(user?.role);
    const canBudget = ["admin", "fleet_manager", "finance"].includes(user?.role);
    const [tab, setTab] = useState("owned"); // owned | hired | notifications | budget | report
    const [machines, setMachines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null);

    const load = async () => {
        try {
            const r = await api.get("/fel/machines");
            setMachines(r.data || []);
        } catch (e) { toast.error(formatApiError(e)); }
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const owned = machines.filter((m) => !m.is_hired);
    const hired = machines.filter((m) => m.is_hired);

    return (
        <div className="space-y-4" data-testid="fleet-dashboard">
            {/* Tab strip */}
            <div className="flex gap-1 overflow-x-auto pb-1 border-b border-slate-200">
                {[
                    { k: "owned", label: "Owned fleet", icon: Truck, badge: owned.length },
                    { k: "hired", label: "Hired fleet", icon: Truck, badge: hired.length },
                    { k: "transporters", label: "Transporters", icon: Truck },
                    { k: "scanner", label: "Vehicle scanner", icon: MagnifyingGlass },
                    { k: "licenses", label: "Licence alerts", icon: Warning },
                    { k: "notifications", label: "Notifications", icon: Bell },
                    canBudget && { k: "budget", label: "Budget", icon: Money },
                    { k: "fuel", label: "Fuel & costs", icon: GasPump },
                    { k: "report", label: "Monthly report", icon: ChartBar },
                ].filter(Boolean).map((t) => (
                    <button
                        key={t.k}
                        onClick={() => setTab(t.k)}
                        className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold uppercase tracking-wide whitespace-nowrap rounded-t-md border-b-2 transition ${
                            tab === t.k
                                ? "border-[var(--grindrod-navy)] text-[var(--grindrod-navy)] bg-slate-50"
                                : "border-transparent text-slate-500 hover:text-slate-700"
                        }`}
                        data-testid={`fleet-tab-${t.k}`}
                    >
                        <t.icon size={13} weight="fill" /> {t.label}
                        {t.badge != null && (
                            <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full bg-slate-200 text-[10px] text-slate-700 font-bold">{t.badge}</span>
                        )}
                    </button>
                ))}
            </div>

            {tab === "owned" && (
                <FleetTab
                    machines={owned}
                    canEdit={canEdit}
                    onRefresh={load}
                    onAdd={() => setModal({ type: "new", isHired: false })}
                    onEdit={(m) => setModal({ type: "edit", machine: m })}
                    onOOS={(m) => setModal({ type: "oos", machine: m })}
                    onInService={async (m) => {
                        try {
                            await api.post(`/fel/machines/${m.id}/in-service`);
                            toast.success(`${m.machine_number} back in service`);
                            load();
                        } catch (e) { toast.error(formatApiError(e)); }
                    }}
                    onDelete={async (m) => {
                        if (!window.confirm(`Delete ${m.machine_number}?`)) return;
                        try {
                            await api.delete(`/fel/machines/${m.id}`);
                            toast.success("Machine removed"); load();
                        } catch (e) { toast.error(formatApiError(e)); }
                    }}
                />
            )}

            {tab === "hired" && (
                <FleetTab
                    machines={hired}
                    canEdit={canEdit}
                    onRefresh={load}
                    onAdd={() => setModal({ type: "new", isHired: true })}
                    onEdit={(m) => setModal({ type: "edit", machine: m })}
                    onOOS={(m) => setModal({ type: "oos", machine: m })}
                    onInService={async (m) => {
                        try {
                            await api.post(`/fel/machines/${m.id}/in-service`);
                            toast.success(`${m.machine_number} back in service`);
                            load();
                        } catch (e) { toast.error(formatApiError(e)); }
                    }}
                    onDelete={async (m) => {
                        if (!window.confirm(`Delete ${m.machine_number}?`)) return;
                        try {
                            await api.delete(`/fel/machines/${m.id}`);
                            toast.success("Hired machine removed"); load();
                        } catch (e) { toast.error(formatApiError(e)); }
                    }}
                    showHireFields
                />
            )}

            {tab === "notifications" && <NotificationsTab canEdit={canEdit} />}

            {tab === "transporters" && <Transporters />}

            {tab === "scanner" && <VehicleScanner />}

            {tab === "licenses" && <FleetLicenseAlerts />}

            {tab === "budget" && canBudget && <BudgetTab user={user} />}

            {tab === "fuel" && <FleetFuelReport />}

            {tab === "report" && <MonthlyReportTab />}

            {modal && (modal.type === "new" || modal.type === "edit") && (
                <MachineModal
                    initial={modal.machine || { is_hired: modal.isHired || false }}
                    onClose={() => setModal(null)}
                    onSaved={() => { setModal(null); load(); }}
                />
            )}
            {modal && modal.type === "oos" && (
                <OutOfServiceModal
                    machine={modal.machine}
                    onClose={() => setModal(null)}
                    onSaved={() => { setModal(null); load(); }}
                />
            )}
        </div>
    );
}

/* ----------------- Fleet tab (Owned / Hired) ----------------- */
function FleetTab({ machines, canEdit, onAdd, onEdit, onOOS, onInService, onDelete, showHireFields }) {
    // Counts by category, in / out service
    const byCat = useMemo(() => {
        const m = {};
        for (const x of machines) {
            const cat = x.category || "other";
            m[cat] = m[cat] || { total: 0, in: 0, out: 0, list: [] };
            m[cat].total += 1;
            if (x.in_service) m[cat].in += 1; else m[cat].out += 1;
            m[cat].list.push(x);
        }
        return m;
    }, [machines]);

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 sm:gap-3">
                {CATEGORY_OPTIONS.map((c) => {
                    const e = byCat[c.value] || { total: 0, in: 0, out: 0 };
                    return (
                        <div key={c.value} className="g-card p-3" data-testid={`fleet-cat-${c.value}`}>
                            <div className="uppercase-label text-[9px]">{c.short}</div>
                            <div className="font-display font-black text-2xl text-[var(--grindrod-navy)] tabular">{e.total}</div>
                            <div className="flex items-center gap-2 text-[11px] mt-0.5">
                                <span className="text-[var(--grindrod-green)] font-semibold">{e.in} in</span>
                                <span className="text-slate-300">·</span>
                                <span className={`font-semibold ${e.out > 0 ? "text-[var(--grindrod-red)]" : "text-slate-400"}`}>{e.out} out</span>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="g-card overflow-hidden">
                <div className="g-card-header-dark">
                    <Truck size={16} weight="fill" />
                    <div className="font-display font-bold uppercase tracking-wide">{showHireFields ? "Hired Fleet" : "Owned Fleet"}</div>
                    {canEdit && (
                        <button onClick={onAdd} className="ml-auto btn-primary !py-1 !px-2 text-xs inline-flex items-center gap-1.5" data-testid="fleet-add-machine">
                            <Plus size={12} weight="bold" /> Add {showHireFields ? "hired " : ""}machine
                        </button>
                    )}
                </div>
                {CATEGORY_OPTIONS.map((c) => {
                    const list = (byCat[c.value]?.list || []).slice().sort((a, b) => a.machine_number.localeCompare(b.machine_number));
                    if (list.length === 0) return null;
                    return (
                        <div key={c.value} data-testid={`fleet-category-block-${c.value}`}>
                            <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-slate-100 border-y border-slate-200">{c.label}</div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs tabular">
                                    <thead className="bg-white text-slate-500 uppercase text-[10px]">
                                        <tr>
                                            <th className="text-left p-2">Machine</th>
                                            <th className="text-left p-2">Make / Model</th>
                                            <th className="text-right p-2">Current hrs</th>
                                            <th className="text-right p-2">Next service in</th>
                                            <th className="text-left p-2">Status</th>
                                            {showHireFields && <th className="text-left p-2">Hire from</th>}
                                            {showHireFields && <th className="text-right p-2">Rate / h</th>}
                                            {canEdit && <th className="p-2"></th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {list.map((m) => {
                                            const status = m.service_status || (m.in_service ? "ok" : "out_of_service");
                                            const next = (m.last_service_hours || 0) + (m.service_interval_hours || 250) - (m.current_hours || 0);
                                            const s = STATUS_LABEL[status] || STATUS_LABEL.ok;
                                            return (
                                                <tr key={m.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`fleet-row-${m.id}`}>
                                                    <td className="p-2 font-display font-bold text-[var(--grindrod-navy)]">
                                                        {m.machine_number}
                                                        {m.owner_division && (
                                                            <span className={`ml-1.5 g-pill normal-case tracking-normal text-[9px] ${m.owner_division === "GTRB" ? "g-pill-navy" : "g-pill-amber"}`}>{m.owner_division}</span>
                                                        )}
                                                    </td>
                                                    <td className="p-2 text-slate-600">{[m.make, m.model].filter(Boolean).join(" / ") || (m.name || "—")}</td>
                                                    <td className="p-2 text-right">{fmtHM(m.current_hours)}</td>
                                                    <td className="p-2 text-right" style={next < 0 ? { color: "var(--grindrod-red)", fontWeight: 700 } : (next < 36 ? { color: "#B36B00", fontWeight: 600 } : undefined)}>
                                                        {next < 0 ? `−${fmtHM(-next)} overdue` : fmtHM(next)}
                                                    </td>
                                                    <td className="p-2">
                                                        <span className="g-pill normal-case tracking-normal" style={{ color: s.colour, background: s.bg }}>{s.label}</span>
                                                        {!m.in_service && (m.out_of_service_reason || m.out_of_service_eta) && (
                                                            <div className="text-[10.5px] text-slate-500 mt-0.5">
                                                                {m.out_of_service_reason}{m.out_of_service_eta ? ` · ETA ${new Date(m.out_of_service_eta).toLocaleDateString()}` : ""}
                                                            </div>
                                                        )}
                                                    </td>
                                                    {showHireFields && <td className="p-2 text-slate-600">{m.hire_company || "—"}</td>}
                                                    {showHireFields && <td className="p-2 text-right tabular">{m.hire_rate_per_hour ? fmtZAR(m.hire_rate_per_hour) : "—"}</td>}
                                                    {canEdit && (
                                                        <td className="p-2 text-right whitespace-nowrap">
                                                            {m.in_service ? (
                                                                <button onClick={() => onOOS(m)} className="text-[11px] text-amber-600 hover:underline mr-2" data-testid={`fleet-oos-${m.id}`}>
                                                                    Mark out
                                                                </button>
                                                            ) : (
                                                                <button onClick={() => onInService(m)} className="text-[11px] text-emerald-600 hover:underline mr-2" data-testid={`fleet-in-service-${m.id}`}>
                                                                    Back in service
                                                                </button>
                                                            )}
                                                            <button onClick={() => onEdit(m)} className="text-slate-500 hover:text-[var(--grindrod-navy)] mr-1.5" title="Edit"><PencilSimple size={13} /></button>
                                                            <button onClick={() => onDelete(m)} className="text-slate-400 hover:text-[var(--grindrod-red)]" title="Delete"><Trash size={13} /></button>
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    );
                })}
                {machines.length === 0 && (
                    <div className="p-8 text-center text-sm text-slate-500">
                        {showHireFields ? "No hired machines registered yet." : "No machines registered yet."}
                    </div>
                )}
            </div>
        </div>
    );
}

/* ----------------- Add/Edit machine modal ----------------- */
function MachineModal({ initial, onClose, onSaved }) {
    const [form, setForm] = useState({
        machine_number: initial.machine_number || "",
        name: initial.name || "",
        make: initial.make || "",
        model: initial.model || "",
        color: initial.color || "",
        registration_plate: initial.registration_plate || "",
        category: initial.category || "fel",
        owner_division: initial.owner_division || "",
        current_hours: initial.current_hours ?? 0,
        service_interval_hours: initial.service_interval_hours ?? 250,
        last_service_hours: initial.last_service_hours ?? 0,
        is_hired: !!initial.is_hired,
        hire_company: initial.hire_company || "",
        hire_rate_per_hour: initial.hire_rate_per_hour ?? "",
        license_disk_number: initial.license_disk_number || "",
        license_expiry_date: initial.license_expiry_date || "",
        notes: initial.notes || "",
    });
    const [saving, setSaving] = useState(false);
    const isEdit = !!initial.id;

    const submit = async (e) => {
        e.preventDefault();
        if (!form.machine_number.trim()) { toast.error("Machine number is required"); return; }
        if (!form.category) { toast.error("Please select a category"); return; }
        if (!form.owner_division) { toast.error("Please select the owner division (GTRB or RBTG)"); return; }
        setSaving(true);
        try {
            const payload = {
                machine_number: form.machine_number.trim(),
                name: form.name || null,
                make: form.make || null,
                model: form.model || null,
                color: form.color || null,
                registration_plate: form.registration_plate || null,
                category: form.category,
                owner_division: form.owner_division,
                current_hours: parseFloat(form.current_hours) || 0,
                service_interval_hours: parseFloat(form.service_interval_hours) || 250,
                last_service_hours: parseFloat(form.last_service_hours) || 0,
                is_hired: !!form.is_hired,
                hire_company: form.is_hired ? (form.hire_company || null) : null,
                hire_rate_per_hour: form.is_hired ? (form.hire_rate_per_hour ? parseFloat(form.hire_rate_per_hour) : null) : null,
                license_disk_number: form.license_disk_number || null,
                license_expiry_date: form.license_expiry_date || null,
                notes: form.notes || null,
            };
            if (isEdit) {
                await api.put(`/fel/machines/${initial.id}`, payload);
                toast.success("Machine updated");
            } else {
                await api.post(`/fel/machines`, payload);
                toast.success("Machine added");
            }
            onSaved();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="machine-modal">
            <div className="bg-white rounded-md w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="g-card-header-dark">
                    <Truck size={16} weight="fill" />
                    <div className="font-display font-bold uppercase tracking-wide flex-1">{isEdit ? "Edit machine" : "Add machine"}</div>
                    <button onClick={onClose} className="text-white"><X size={16} /></button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-3">
                    <div className="grid md:grid-cols-2 gap-3">
                        <div>
                            <label className="uppercase-label block mb-1">Machine number <span className="text-[var(--grindrod-red)]">*</span></label>
                            <input value={form.machine_number} onChange={(e) => setForm({ ...form, machine_number: e.target.value })} className="g-input" placeholder="e.g. FEL-12" data-testid="m-number" />
                        </div>
                        <div>
                            <label className="uppercase-label block mb-1">Category <span className="text-[var(--grindrod-red)]">*</span></label>
                            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="g-input" data-testid="m-category">
                                {CATEGORY_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="uppercase-label block mb-1">Owner division <span className="text-[var(--grindrod-red)]">*</span></label>
                            <div className="grid grid-cols-2 gap-2" data-testid="m-owner-division">
                                {[
                                    { key: "GTRB", title: "GTRB", sub: "Grindrod Terminals Richards Bay" },
                                    { key: "RBTG", title: "RBTG", sub: "Richards Bay Terminals Grindrod" },
                                ].map((d) => (
                                    <button
                                        key={d.key}
                                        type="button"
                                        onClick={() => setForm({ ...form, owner_division: d.key })}
                                        className={`text-left p-2 rounded-md border-2 transition ${
                                            form.owner_division === d.key
                                                ? "border-[var(--grindrod-navy)] bg-[var(--grindrod-navy)]/5"
                                                : "border-slate-200 hover:border-slate-300"
                                        }`}
                                        data-testid={`m-owner-division-${d.key}`}
                                    >
                                        <div className={`font-display font-black text-sm ${form.owner_division === d.key ? "text-[var(--grindrod-navy)]" : "text-slate-700"}`}>{d.title}</div>
                                        <div className="text-[10px] text-slate-500">{d.sub}</div>
                                    </button>
                                ))}
                            </div>
                            <div className="text-[10.5px] text-slate-500 mt-1">
                                Used for cost-recovery tracking when this machine operates for the other division.
                            </div>
                        </div>
                        <div>
                            <label className="uppercase-label block mb-1">Name / Description</label>
                            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="g-input" placeholder="optional" />
                        </div>
                        <div>
                            <label className="uppercase-label block mb-1">Make / Model</label>
                            <div className="grid grid-cols-2 gap-2">
                                <input value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} className="g-input" placeholder="Make" data-testid="m-make" />
                                <input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="g-input" placeholder="Model" data-testid="m-model" />
                            </div>
                        </div>
                        <div>
                            <label className="uppercase-label block mb-1">Colour / Registration plate</label>
                            <div className="grid grid-cols-2 gap-2">
                                <input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="g-input" placeholder="Colour" data-testid="m-color" />
                                <input value={form.registration_plate} onChange={(e) => setForm({ ...form, registration_plate: e.target.value.toUpperCase() })} className="g-input font-mono" placeholder="Plate (optional)" data-testid="m-registration-plate" />
                            </div>
                        </div>
                        <div>
                            <label className="uppercase-label block mb-1">Licence disk number</label>
                            <input value={form.license_disk_number} onChange={(e) => setForm({ ...form, license_disk_number: e.target.value })} className="g-input" placeholder="e.g. 12ABC3456" data-testid="m-lic-num" />
                        </div>
                        <div>
                            <label className="uppercase-label block mb-1">Licence disk expiry</label>
                            <input type="date" value={form.license_expiry_date} onChange={(e) => setForm({ ...form, license_expiry_date: e.target.value })} className="g-input" data-testid="m-lic-expiry" />
                        </div>
                        <div>
                            <label className="uppercase-label block mb-1">Current hours</label>
                            <input type="number" step="0.1" min="0" value={form.current_hours} onChange={(e) => setForm({ ...form, current_hours: e.target.value })} className="g-input tabular" />
                        </div>
                        <div>
                            <label className="uppercase-label block mb-1">Last service @ hours</label>
                            <input type="number" step="0.1" min="0" value={form.last_service_hours} onChange={(e) => setForm({ ...form, last_service_hours: e.target.value })} className="g-input tabular" />
                        </div>
                        <div>
                            <label className="uppercase-label block mb-1">Service interval (hours)</label>
                            <input type="number" step="1" min="1" value={form.service_interval_hours} onChange={(e) => setForm({ ...form, service_interval_hours: e.target.value })} className="g-input tabular" />
                        </div>
                        <div className="flex items-end">
                            <label className="inline-flex items-center gap-2 text-xs font-semibold">
                                <input type="checkbox" checked={form.is_hired} onChange={(e) => setForm({ ...form, is_hired: e.target.checked })} data-testid="m-is-hired" />
                                This is a HIRED machine (rented from third party)
                            </label>
                        </div>
                        {form.is_hired && (
                            <>
                                <div>
                                    <label className="uppercase-label block mb-1">Hire company</label>
                                    <input value={form.hire_company} onChange={(e) => setForm({ ...form, hire_company: e.target.value })} className="g-input" placeholder="e.g. CAT Rental" />
                                </div>
                                <div>
                                    <label className="uppercase-label block mb-1">Hire rate (R / hour)</label>
                                    <input type="number" step="1" min="0" value={form.hire_rate_per_hour} onChange={(e) => setForm({ ...form, hire_rate_per_hour: e.target.value })} className="g-input tabular" />
                                </div>
                            </>
                        )}
                    </div>
                    <div>
                        <label className="uppercase-label block mb-1">Notes</label>
                        <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="g-input" />
                    </div>
                    <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                        <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
                        <button type="submit" disabled={saving} className="btn-primary" data-testid="m-save">{saving ? "Saving…" : isEdit ? "Save" : "Add machine"}</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

/* ----------------- Out of service modal ----------------- */
function OutOfServiceModal({ machine, onClose, onSaved }) {
    const [reason, setReason] = useState("");
    const [eta, setEta] = useState("");
    const [saving, setSaving] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        if (!reason.trim()) { toast.error("Reason is required"); return; }
        setSaving(true);
        try {
            await api.post(`/fel/machines/${machine.id}/out-of-service`, {
                reason: reason.trim(),
                eta: eta ? new Date(eta).toISOString() : null,
            });
            toast.success(`${machine.machine_number} marked out of service`);
            onSaved();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };
    return (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="oos-modal">
            <div className="bg-white rounded-md w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                <div className="g-card-header-red">
                    <Power size={16} weight="fill" />
                    <div className="font-display font-bold uppercase tracking-wide flex-1">Mark out of service</div>
                    <button onClick={onClose} className="text-white"><X size={16} /></button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-3">
                    <div className="text-sm text-slate-600">Machine: <b className="text-[var(--grindrod-navy)]">{machine.machine_number}</b></div>
                    <div>
                        <label className="uppercase-label block mb-1">Reason <span className="text-[var(--grindrod-red)]">*</span></label>
                        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="g-input" placeholder="e.g. Gearbox failure, awaiting parts" data-testid="oos-reason" />
                    </div>
                    <div>
                        <label className="uppercase-label block mb-1">ETA back in service (optional)</label>
                        <input type="datetime-local" value={eta} onChange={(e) => setEta(e.target.value)} className="g-input" data-testid="oos-eta" />
                    </div>
                    <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                        <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
                        <button type="submit" disabled={saving} className="btn-primary" data-testid="oos-save">{saving ? "Saving…" : "Mark out of service"}</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

/* ----------------- Notifications tab ----------------- */
function NotificationsTab({ canEdit }) {
    const [items, setItems] = useState([]);
    const [unresolved, setUnresolved] = useState(0);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("unresolved");
    const load = async () => {
        try {
            const r = await api.get("/fleet/notifications", { params: { unresolved_only: filter === "unresolved" } });
            setItems(r.data.items || []);
            setUnresolved(r.data.unresolved_count || 0);
        } catch (e) { toast.error(formatApiError(e)); }
        setLoading(false);
    };
    useEffect(() => { load(); }, [filter]);
    return (
        <div className="g-card overflow-hidden" data-testid="fleet-notifications">
            <div className="g-card-header-dark">
                <Bell size={16} weight="fill" />
                <div className="font-display font-bold uppercase tracking-wide flex-1">Fleet notifications</div>
                <span className="text-xs">Unresolved: <b>{unresolved}</b></span>
                <select value={filter} onChange={(e) => setFilter(e.target.value)} className="ml-3 g-input !py-1 !text-[11px]">
                    <option value="unresolved">Unresolved</option>
                    <option value="all">All</option>
                </select>
            </div>
            {items.length === 0 && !loading && (
                <div className="p-8 text-center text-sm text-slate-500">No fleet notifications.</div>
            )}
            <ul className="divide-y divide-slate-100">
                {items.map((n) => (
                    <li key={n.id} className={`px-4 py-3 flex items-start gap-3 ${n.resolved ? "opacity-60" : ""}`} data-testid={`fleet-notif-${n.id}`}>
                        <div className={`w-2 h-2 mt-2 rounded-full ${
                            n.kind === "breakdown_reported" ? "bg-[var(--grindrod-red)]" :
                            n.kind === "out_of_service" ? "bg-amber-500" : "bg-emerald-500"
                        }`} />
                        <div className="flex-1 min-w-0">
                            <div className="font-display font-bold text-sm text-[var(--grindrod-navy)]">{n.title}</div>
                            <div className="text-xs text-slate-600 whitespace-pre-wrap">{n.body}</div>
                            <div className="text-[10.5px] text-slate-400 mt-1">
                                {n.actor ? `${n.actor} · ` : ""}{new Date(n.created_at).toLocaleString()}
                                {n.resolved && ` · resolved by ${n.resolved_by} at ${new Date(n.resolved_at).toLocaleString()}`}
                            </div>
                        </div>
                        {canEdit && !n.resolved && (
                            <button
                                onClick={async () => {
                                    try { await api.post(`/fleet/notifications/${n.id}/resolve`); toast.success("Resolved"); load(); }
                                    catch (e) { toast.error(formatApiError(e)); }
                                }}
                                className="text-[11px] text-emerald-600 hover:underline"
                                data-testid={`resolve-${n.id}`}
                            >
                                Resolve
                            </button>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
}

/* ----------------- Budget tab ----------------- */
function BudgetTab() {
    const [entries, setEntries] = useState([]);
    const [modal, setModal] = useState(null);
    const [loading, setLoading] = useState(true);
    const load = async () => {
        try {
            const r = await api.get("/fleet/budget");
            setEntries(r.data || []);
        } catch (e) { toast.error(formatApiError(e)); }
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    return (
        <div className="g-card overflow-hidden" data-testid="fleet-budget-tab">
            <div className="g-card-header-dark">
                <Money size={16} weight="fill" />
                <div className="font-display font-bold uppercase tracking-wide flex-1">Fleet budget</div>
                <button onClick={() => setModal({})} className="btn-primary !py-1 !px-2 text-xs inline-flex items-center gap-1.5" data-testid="fleet-budget-add">
                    <Plus size={12} weight="bold" /> Add month
                </button>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-xs tabular">
                    <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                        <tr>
                            <th className="text-left p-2">Period</th>
                            <th className="text-left p-2">Division</th>
                            <th className="text-right p-2">Breakdown</th>
                            <th className="text-right p-2">Equipment hire</th>
                            <th className="text-right p-2">Maintenance</th>
                            <th className="text-right p-2">Fuel litres</th>
                            <th className="text-right p-2">Fuel cost</th>
                            <th className="p-2"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {entries.map((b) => (
                            <tr key={b.id} className="border-t border-slate-100 hover:bg-slate-50">
                                <td className="p-2 font-semibold">{b.period_label}</td>
                                <td className="p-2">{b.division}</td>
                                <td className="p-2 text-right">{fmtZAR(b.breakdown_cost_actual_zar)} / <span className="text-slate-400">{fmtZAR(b.breakdown_cost_budget_zar)}</span></td>
                                <td className="p-2 text-right">{fmtZAR(b.equipment_hire_actual_zar)} / <span className="text-slate-400">{fmtZAR(b.equipment_hire_budget_zar)}</span></td>
                                <td className="p-2 text-right">{fmtZAR(b.maintenance_actual_zar)} / <span className="text-slate-400">{fmtZAR(b.maintenance_budget_zar)}</span></td>
                                <td className="p-2 text-right">{Number(b.fuel_litres_actual || 0).toLocaleString()} L / <span className="text-slate-400">{Number(b.fuel_litres_budget || 0).toLocaleString()} L</span></td>
                                <td className="p-2 text-right">{fmtZAR(b.fuel_cost_actual_zar)}</td>
                                <td className="p-2 text-right">
                                    <button onClick={() => setModal(b)} className="text-[var(--grindrod-navy)] hover:underline text-[11px]">Edit</button>
                                </td>
                            </tr>
                        ))}
                        {!loading && entries.length === 0 && (
                            <tr><td colSpan={8} className="p-6 text-center text-sm text-slate-500">No budget entries yet.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            {modal && <BudgetModal initial={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
        </div>
    );
}

function BudgetModal({ initial, onClose, onSaved }) {
    const monthDefault = new Date().toISOString().slice(0, 7);
    const [form, setForm] = useState({
        period_label: initial.period_label || monthDefault,
        division: initial.division || "GTRB",
        breakdown_cost_budget_zar: initial.breakdown_cost_budget_zar ?? 0,
        breakdown_cost_actual_zar: initial.breakdown_cost_actual_zar ?? 0,
        equipment_hire_budget_zar: initial.equipment_hire_budget_zar ?? 0,
        equipment_hire_actual_zar: initial.equipment_hire_actual_zar ?? 0,
        maintenance_budget_zar: initial.maintenance_budget_zar ?? 0,
        maintenance_actual_zar: initial.maintenance_actual_zar ?? 0,
        fuel_litres_budget: initial.fuel_litres_budget ?? 0,
        fuel_litres_actual: initial.fuel_litres_actual ?? 0,
        fuel_cost_actual_zar: initial.fuel_cost_actual_zar ?? 0,
        notes: initial.notes || "",
    });
    const [saving, setSaving] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                period_label: form.period_label,
                division: form.division,
                breakdown_cost_budget_zar: parseFloat(form.breakdown_cost_budget_zar) || 0,
                breakdown_cost_actual_zar: parseFloat(form.breakdown_cost_actual_zar) || 0,
                equipment_hire_budget_zar: parseFloat(form.equipment_hire_budget_zar) || 0,
                equipment_hire_actual_zar: parseFloat(form.equipment_hire_actual_zar) || 0,
                maintenance_budget_zar: parseFloat(form.maintenance_budget_zar) || 0,
                maintenance_actual_zar: parseFloat(form.maintenance_actual_zar) || 0,
                fuel_litres_budget: parseFloat(form.fuel_litres_budget) || 0,
                fuel_litres_actual: parseFloat(form.fuel_litres_actual) || 0,
                fuel_cost_actual_zar: parseFloat(form.fuel_cost_actual_zar) || 0,
                notes: form.notes || null,
            };
            await api.post("/fleet/budget", payload);
            toast.success("Budget saved");
            onSaved();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };
    return (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="budget-modal">
            <div className="bg-white rounded-md w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="g-card-header-dark">
                    <Money size={16} weight="fill" />
                    <div className="font-display font-bold uppercase tracking-wide flex-1">Fleet budget · {form.period_label}</div>
                    <button onClick={onClose} className="text-white"><X size={16} /></button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-3">
                    <div className="grid md:grid-cols-2 gap-3">
                        <div>
                            <label className="uppercase-label block mb-1">Month (YYYY-MM)</label>
                            <input type="month" value={form.period_label} onChange={(e) => setForm({ ...form, period_label: e.target.value })} className="g-input" required />
                        </div>
                        <div>
                            <label className="uppercase-label block mb-1">Division</label>
                            <select value={form.division} onChange={(e) => setForm({ ...form, division: e.target.value })} className="g-input">
                                <option value="GTRB">GTRB</option>
                                <option value="RBTG">RBTG</option>
                            </select>
                        </div>
                    </div>

                    <Section title="Breakdown costs (R)">
                        <Pair budget label="Budget" v={form.breakdown_cost_budget_zar} setV={(v) => setForm({ ...form, breakdown_cost_budget_zar: v })} />
                        <Pair label="Actual" v={form.breakdown_cost_actual_zar} setV={(v) => setForm({ ...form, breakdown_cost_actual_zar: v })} />
                    </Section>
                    <Section title="Equipment hire (R)">
                        <Pair budget label="Budget" v={form.equipment_hire_budget_zar} setV={(v) => setForm({ ...form, equipment_hire_budget_zar: v })} />
                        <Pair label="Actual" v={form.equipment_hire_actual_zar} setV={(v) => setForm({ ...form, equipment_hire_actual_zar: v })} />
                    </Section>
                    <Section title="Maintenance (R)">
                        <Pair budget label="Budget" v={form.maintenance_budget_zar} setV={(v) => setForm({ ...form, maintenance_budget_zar: v })} />
                        <Pair label="Actual" v={form.maintenance_actual_zar} setV={(v) => setForm({ ...form, maintenance_actual_zar: v })} />
                    </Section>
                    <Section title="Fuel">
                        <Pair label="Litres budgeted" v={form.fuel_litres_budget} setV={(v) => setForm({ ...form, fuel_litres_budget: v })} suffix="L" />
                        <Pair label="Litres actual" v={form.fuel_litres_actual} setV={(v) => setForm({ ...form, fuel_litres_actual: v })} suffix="L" />
                        <Pair label="Total fuel cost (R)" v={form.fuel_cost_actual_zar} setV={(v) => setForm({ ...form, fuel_cost_actual_zar: v })} />
                    </Section>

                    <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                        <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
                        <button type="submit" disabled={saving} className="btn-primary" data-testid="budget-save">{saving ? "Saving…" : "Save budget"}</button>
                    </div>
                </form>
            </div>
        </div>
    );
}
function Section({ title, children }) {
    return (
        <div>
            <div className="uppercase-label text-[10px] mb-1.5">{title}</div>
            <div className="grid grid-cols-2 gap-2">{children}</div>
        </div>
    );
}
function Pair({ label, v, setV, budget, suffix }) {
    return (
        <div>
            <label className={`text-[10px] uppercase ${budget ? "text-slate-500" : "text-slate-700 font-semibold"}`}>{label}</label>
            <div className="relative">
                <input type="number" step="0.01" min="0" value={v} onChange={(e) => setV(e.target.value)} className="g-input tabular" />
                {suffix && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">{suffix}</span>}
            </div>
        </div>
    );
}

/* ----------------- Monthly report tab ----------------- */
function MonthlyReportTab() {
    const monthDefault = new Date().toISOString().slice(0, 7);
    const [period, setPeriod] = useState(monthDefault);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async (lbl) => {
        try {
            const r = await api.get("/fleet/report", { params: { period_label: lbl } });
            setReport(r.data);
        } catch (e) { toast.error(formatApiError(e)); }
        setLoading(false);
    };
    useEffect(() => { load(period); /* eslint-disable-next-line */ }, [period]);

    return (
        <div className="space-y-4" data-testid="fleet-report-tab">
            <div className="g-card overflow-hidden">
                <div className="g-card-header-dark">
                    <ChartBar size={16} weight="fill" />
                    <div className="font-display font-bold uppercase tracking-wide flex-1">Fleet monthly report</div>
                    <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="g-input !py-1 !text-[11px] bg-white text-slate-900" />
                    <button onClick={() => load(period)} className="ml-2 text-white hover:opacity-80" title="Refresh"><ArrowsCounterClockwise size={14} /></button>
                </div>

                {report && (
                    <div className="p-4 space-y-4">
                        {/* Top cards */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 sm:gap-3">
                            <SmallStat Icon={Money} label="Budget total" value={fmtZAR(report.budget.total_budget_zar)} />
                            <SmallStat Icon={Money} label="Actual total" value={fmtZAR(report.budget.total_actual_zar)}
                                       accent={report.budget.total_actual_zar > report.budget.total_budget_zar ? "red" : "green"} />
                            <SmallStat Icon={GasPump} label="Fuel actual" value={`${Math.round(report.budget.fuel_litres_actual).toLocaleString()} L`} />
                            <SmallStat Icon={Clock} label="GTRB hours" value={fmtMinHM(report.by_division?.GTRB?.hours_minutes)} />
                            <SmallStat Icon={Clock} label="RBTG hours" value={fmtMinHM(report.by_division?.RBTG?.hours_minutes)} />
                        </div>

                        {/* Cost recovery card — cross-division machine hours */}
                        {report.cost_recovery && (
                            <div className="g-card overflow-hidden" data-testid="cost-recovery-card">
                                <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-2">
                                    <ArrowsCounterClockwise size={12} weight="fill" /> Cost recovery · cross-division machine hours
                                </div>
                                <div className="grid grid-cols-2 gap-px bg-slate-200">
                                    <div className="bg-white p-3" data-testid="cost-recovery-gtrb-rbtg">
                                        <div className="text-[10px] uppercase tracking-wider text-slate-500">GTRB-owned worked for RBTG</div>
                                        <div className="font-display font-black text-2xl tabular text-[var(--grindrod-navy)]">
                                            {fmtMinHM(report.cost_recovery.GTRB_to_RBTG_minutes)}
                                        </div>
                                        <div className="text-[10.5px] text-slate-500 mt-0.5">RBTG owes GTRB for these hours</div>
                                    </div>
                                    <div className="bg-white p-3" data-testid="cost-recovery-rbtg-gtrb">
                                        <div className="text-[10px] uppercase tracking-wider text-slate-500">RBTG-owned worked for GTRB</div>
                                        <div className="font-display font-black text-2xl tabular" style={{ color: "#B36B00" }}>
                                            {fmtMinHM(report.cost_recovery.RBTG_to_GTRB_minutes)}
                                        </div>
                                        <div className="text-[10.5px] text-slate-500 mt-0.5">GTRB owes RBTG for these hours</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Bar chart */}
                        <div className="g-card overflow-hidden">
                            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500 font-bold">
                                Last 12 months — budget vs actual vs fuel
                            </div>
                            <div style={{ height: 260 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={report.monthly_series} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                        <XAxis dataKey="period_label" tick={{ fontSize: 10 }} />
                                        <YAxis yAxisId="zar" tick={{ fontSize: 10 }} tickFormatter={(v) => `R${Math.round(v/1000)}k`} />
                                        <YAxis yAxisId="fuel" orientation="right" tick={{ fontSize: 10 }} tickFormatter={(v) => `${Math.round(v)}L`} />
                                        <Tooltip formatter={(v, name) => name === "Fuel L" ? `${Math.round(v)} L` : fmtZAR(v)} />
                                        <Legend wrapperStyle={{ fontSize: 11 }} />
                                        <Bar yAxisId="zar" dataKey="budget_zar" name="Budget" fill="#0F172A" />
                                        <Bar yAxisId="zar" dataKey="actual_zar" name="Actual" fill="#E3000F" />
                                        <Bar yAxisId="fuel" dataKey="fuel_litres" name="Fuel L" fill="#10B981" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* Machine utilisation table */}
                        <div className="g-card overflow-hidden">
                            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500 font-bold">
                                Machines utilised this month
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs tabular" data-testid="fleet-report-machines">
                                    <thead className="bg-white text-slate-500 uppercase text-[10px]">
                                        <tr>
                                            <th className="text-left p-2">Machine</th>
                                            <th className="text-left p-2">Category</th>
                                            <th className="text-left p-2">Owner</th>
                                            <th className="text-left p-2">Type</th>
                                            <th className="text-right p-2">Total hours</th>
                                            <th className="text-right p-2">GTRB</th>
                                            <th className="text-right p-2">RBTG</th>
                                            <th className="text-right p-2">Cross-div hrs</th>
                                            <th className="text-right p-2">Sessions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(report.machines || []).map((m) => (
                                            <tr key={m.machine_number} className="border-t border-slate-100">
                                                <td className="p-2 font-bold text-[var(--grindrod-navy)]">{m.machine_number}</td>
                                                <td className="p-2 capitalize">{CATEGORY_LABEL[m.category] || "—"}</td>
                                                <td className="p-2">
                                                    {m.owner_division && (
                                                        <span className={`g-pill normal-case tracking-normal ${m.owner_division === "GTRB" ? "g-pill-navy" : "g-pill-amber"}`}>{m.owner_division}</span>
                                                    )}
                                                </td>
                                                <td className="p-2">{m.is_hired
                                                    ? <span className="g-pill normal-case tracking-normal" style={{ color: "#B36B00", background: "#FFF8E1" }}>Hired</span>
                                                    : <span className="g-pill normal-case tracking-normal" style={{ color: "var(--grindrod-navy)", background: "#E2E8F0" }}>Owned</span>}</td>
                                                <td className="p-2 text-right font-semibold tabular">{fmtMinHM(m.total_minutes)}</td>
                                                <td className="p-2 text-right">{fmtMinHM(m.by_division?.GTRB || 0)}</td>
                                                <td className="p-2 text-right">{fmtMinHM(m.by_division?.RBTG || 0)}</td>
                                                <td className={`p-2 text-right tabular ${m.cross_division_minutes > 0 ? "text-[var(--grindrod-red)] font-bold" : "text-slate-400"}`}>
                                                    {fmtMinHM(m.cross_division_minutes || 0)}
                                                </td>
                                                <td className="p-2 text-right">{m.session_count}</td>
                                            </tr>
                                        ))}
                                        {(report.machines || []).length === 0 && (
                                            <tr><td colSpan={9} className="p-6 text-center text-sm text-slate-500">No machine activity this month.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function SmallStat({ Icon, label, value, accent }) {
    const color = accent === "red" ? "var(--grindrod-red)" : accent === "green" ? "var(--grindrod-green)" : "var(--grindrod-navy)";
    return (
        <div className="g-card p-3">
            <div className="flex items-center gap-2 mb-1">
                <Icon size={14} weight="fill" style={{ color }} />
                <div className="uppercase-label text-[9.5px]">{label}</div>
            </div>
            <div className="font-display font-black text-lg tabular" style={{ color }}>{value}</div>
        </div>
    );
}

function StatCard({ Icon, label, value, accent, testid }) {
    return (
        <div className="g-card p-3" data-testid={testid}>
            <div className="flex items-center gap-2 mb-1">
                <Icon size={14} weight="fill" style={{ color: accent }} />
                <div className="uppercase-label text-[9.5px]">{label}</div>
            </div>
            <div className="font-display font-black text-2xl tabular" style={{ color: accent }}>{value}</div>
        </div>
    );
}
